import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  Radio,
  RadioGroup,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Flex, Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FaEdit, FaTrash, GrUpdate } from 'react-icons/all';
import CreatableSelect from 'react-select/creatable';
import {
  Input,
  Pagination,
  ReactSelect,
  StatusBadge,
  Table,
} from '~/components';
import { SelectItem, Pagination as PaginationType } from '~/types';
import { useFilialAsSelect } from '~/hooks';
import {
  getMachineParkList,
  getTotalMachineParkList,
  removeMachinePark,
  useEquipmentTypesAsSelect,
  useManufacturerAsSelect,
} from '~/hooks/useStrategicMap';
import {
  idadePaqueLista,
  MachineParkCreate,
  MachineParkView,
  tipoAquisicaoParqueMaquinasLista,
} from '~/types/strategicMap';
import api from '~/utils/api';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { PAGE_SIZE } from '~/constants';
import { queryClient } from '~/utils/react-query';

export const MachineParkForm: React.FC<{
  grupo?: SelectItem;
}> = ({ grupo }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenDialog,
    onOpen: onOpenDialog,
    onClose: onCloseDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenConfirmation,
    onOpen: onOpenConfirmation,
    onClose: onCloseConfirmation,
  } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const [isFetching, setFetching] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { errors } = formState;

  const {
    data: filialList,
    isLoading: isFilialLoading,
    refetch: refetchFilial,
  } = useFilialAsSelect({}, [], grupo ? [grupo] : [], []);
  const [filial, setFilial] = useState<SelectItem | undefined>(undefined);

  const [idadeParqueList, setIdadeParqueList] =
    useState<SelectItem[]>(idadePaqueLista);
  const [idadeParque, setIdadeParque] = useState<SelectItem | undefined>(
    undefined,
  );
  const {
    data: tipoEquipamentoList,
    isLoading: isTipoEquipamentoLoading,
    refetch: refetchTipoEquipamento,
  } = useEquipmentTypesAsSelect();
  const [tipoEquipamento, setTipoEquipamento] = useState<
    SelectItem | undefined
  >(undefined);

  const [formattedFabricanteList, setFormattedFabricanteList] = useState<
    SelectItem[]
  >([]);
  const {
    data: fabricanteList,
    isLoading: isFabricanteLoading,
    refetch: refetchFabricante,
  } = useManufacturerAsSelect({
    onSuccess: data => {
      const fabricantes = data.map(it => ({
        value: it.value,
        label: it.label,
      }));
      setFormattedFabricanteList(
        [
          {
            label: 'Desconhecido',
            value: '0',
          },
        ].concat(fabricantes) as SelectItem[],
      );
    },
  });
  const [fabricante, setFabricante] = useState<SelectItem | undefined>({
    label: 'Desconhecido',
    value: '0',
  });

  const [tipoAquisicaoList, setTipoAquisicaoList] = useState<SelectItem[]>(
    tipoAquisicaoParqueMaquinasLista,
  );
  const [tipoAquisicao, setTipoAquisicao] = useState<SelectItem | undefined>(
    undefined,
  );

  const [quantidade, setQuantidade] = useState<number | undefined>(0);
  const [descricao, setDescricao] = useState<string>();
  const [fornecedor, setFornecedor] = useState<string>();
  const [parqueMaquinas, setParqueMaquinas] =
    useState<PaginationType<MachineParkView>>();
  const [idEdicao, setIdEdicao] = useState<number | undefined>(undefined);
  const [idRemover, setIdRemover] = useState<number>(0);

  const [parqueAtivo, setParqueAtivo] = useState<string>('true');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const handleOpen = () => {
    setFetching(true);
    loadParqueMaquinas();
    loadTotalParqueMaquinas();
    refetchTipoEquipamento();
    refetchFabricante();
    onOpen();
  };
  const loadTotalParqueMaquinas = () => {
    getTotalMachineParkList(Number(grupo!.value)).then(result => {
      setTotal(result);
    });
  };
  const loadParqueMaquinas = (_page?: number) => {
    getMachineParkList(Number(grupo!.value), _page ?? page)
      .then(_machineParks => {
        const formatted: MachineParkView[] = _machineParks.results.map(
          item => ({
            ...item,
            statusStr: item.status === 1 ? 'Ativo' : 'Inativo',
            statusColor: item.status === 1 ? 'green' : 'red',
            dataFormatada: item.dt_ultimaatualizacao
              ? formatInTimeZone(`${item.dt_ultimaatualizacao}`, 'dd/MM/yyyy')
              : '-',
            aquisicaoFormatado:
              tipoAquisicaoParqueMaquinasLista.find(
                it => it.value.toString() === item.tipo_aquisicao.toString(),
              )?.label || '-',
            idadeFormatada:
              idadePaqueLista.find(
                it => it.value.toString() === item.idade?.toString(),
              )?.label || '-',
          }),
        );
        setParqueMaquinas({
          ..._machineParks,
          results: formatted,
        });
      })
      .finally(() => {
        setFetching(false);
      });
  };

  function handleFilial(e?: SelectItem) {
    setFilial(e);
    if (Number(e?.value) !== 0) {
      clearErrors('filial');
    }
  }
  function handleFabricante(e?: SelectItem) {
    setFabricante(e);
    if (Number(e?.value) !== 0) {
      clearErrors('fabricante');
    }
  }
  function handleQuantidade(e: any) {
    clearErrors('quantidade');
    if (e && e.length > 12) {
      return;
    }
    const value = e.replace(/\D/g, '');
    setQuantidade(Number(value) || undefined);
  }
  function handleIdadeParque(e?: SelectItem) {
    setIdadeParque(e);
    if (Number(e?.value) !== 0) {
      clearErrors('idadeParque');
    }
  }
  function handleTipoEquipamento(e?: SelectItem) {
    setTipoEquipamento(e);
    if (Number(e?.value) !== 0) {
      clearErrors('tipoEquipamento');
    }
  }
  function handleTipoAquisicao(e?: SelectItem) {
    setTipoAquisicao(e);
    if (Number(e?.value) !== undefined) {
      clearErrors('tipoAquisicao');
    }
  }
  function handleDescricao(e: any) {
    setDescricao(e);
    clearErrors('descricao');
    if (e.length > 255) {
      setError('descricao', {
        type: 'manual',
        message: 'Campo deve ter no máximo 255 caracteres',
      });
    }
  }
  function handleFornecedor(e: any) {
    setFornecedor(e);
    clearErrors('fornecedor');
    if (e.length > 255) {
      setError('fornecedor', {
        type: 'manual',
        message: 'Campo deve ter no máximo 255 caracteres',
      });
    }
  }
  const handleClose = () => {
    clearValues();
    limpar();
    onClose();
    queryClient.invalidateQueries('piechartequipmentgeral');
    queryClient.invalidateQueries('donutchartequipmentcompeting');
    queryClient.invalidateQueries('donutchartequipmentpositivoandcompeting');
    queryClient.invalidateQueries('barchartequipmentpositivo');
    queryClient.invalidateQueries('barchartequipmentconcorrentes');
  };

  const clearValues = () => {
    clearErrors();
  };

  const validar = () => {
    let isValid = true;
    if (!tipoEquipamento) {
      setError('tipoEquipamento', {
        type: 'manual',
        message: 'Campo obrigatório',
      });
      isValid = false;
    }
    if (!tipoAquisicao) {
      setError('tipoAquisicao', {
        type: 'manual',
        message: 'Campo obrigatório',
      });
      isValid = false;
    }
    if (!descricao || !descricao.trim()) {
      setError('descricao', {
        type: 'manual',
        message: 'Campo obrigatório',
      });
      isValid = false;
    }
    if (descricao && descricao.length > 255) {
      setError('descricao', {
        type: 'manual',
        message: 'Campo deve ter no máximo 255 caracteres',
      });
      isValid = false;
    }
    if (fornecedor && fornecedor.length > 255) {
      setError('fornecedor', {
        type: 'manual',
        message: 'Campo deve ter no máximo 255 caracteres',
      });
      isValid = false;
    }
    if (!quantidade || quantidade === 0 || quantidade.toString() === '0') {
      setError('quantidade', {
        type: 'manual',
        message: 'Campo obrigatório',
      });
      isValid = false;
    }
    return isValid;
  };

  async function handleCreate() {
    clearErrors();

    if (!validar()) return;

    const data = {} as unknown as MachineParkCreate;
    data.empresa = Number(filial?.value);
    data.fabricante =
      Number(fabricante?.value) === 0 ? undefined : Number(fabricante?.value);
    data.tipo_equipamento = Number(tipoEquipamento?.value);
    data.idade = Number(idadeParque?.value);
    data.tipo_aquisicao = Number(tipoAquisicao?.value);
    data.quantidade = quantidade!;
    data.descricao = descricao!;
    data.fornecedor = fornecedor ? fornecedor.trim() : undefined;
    data.grupo_economico = Number(grupo?.value);
    data.id = isEdit ? idEdicao : undefined;

    const request = isEdit
      ? api.put(`/parque_maquinas/${idEdicao}`, data)
      : api.post(`/parque_maquinas`, data);

    request
      .then(() => {
        toast({
          title: `Parque de máquinas ${
            isEdit ? 'atualizado' : 'cadastrado'
          } com sucesso`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setFetching(false);
        loadParqueMaquinas();
        loadTotalParqueMaquinas();
        limpar();
      })
      .catch(() => {
        toast({
          title: `Erro ao ${
            isEdit ? 'atualizar' : 'cadastrar'
          } parque de máquinas`,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setFetching(false);
      });

    setFetching(true);
  }

  const removeMachineParkFunc = () => {
    setFetching(true);
    removeMachinePark(idRemover)
      .then(() => {
        toast({
          title: 'Parque de máquinas removido com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setFetching(false);
        loadParqueMaquinas();
        loadTotalParqueMaquinas();
        onCloseConfirmation();
        if (page > 1 && parqueMaquinas?.results?.length === 1) {
          setPage(page - 1);
          loadParqueMaquinas(page - 1);
        }
      })
      .catch(() => {
        toast({
          title: 'Erro ao remover parque de máquinas',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setFetching(false);
      });
  };

  const limpar = () => {
    reset();
    setFilial(undefined);
    setFabricante(
      formattedFabricanteList.find(it => it.value === '0') || undefined,
    );
    setTipoEquipamento(undefined);
    setIdadeParque(undefined);
    setTipoAquisicao(undefined);
    setDescricao('');
    setFornecedor('');
    setQuantidade(0);
    setIdEdicao(undefined);
    setIsEdit(false);
    setPage(1);
  };

  const editar = (item: MachineParkView) => {
    clearErrors();

    setFilial({
      value: String(item.empresa?.id_empresa),
      label: item.empresa?.nm_razaosocial || '-',
    });
    setFabricante(
      item.fabricante
        ? {
            value: String(item.fabricante?.id),
            label: item.fabricante?.nome || '-',
          }
        : {
            value: '0',
            label: 'Desconhecido',
          },
    );
    setTipoEquipamento({
      value: String(item.tipo_equipamento.id_tipoequipamento),
      label: item.tipo_equipamento.nm_tipoequipamento,
    });
    const idade = idadeParqueList.find(it => {
      return it.label === item.idade;
    });
    setIdadeParque({
      value: String(idade?.value),
      label: idade?.label || '-',
    });
    const aquisicao = tipoAquisicaoParqueMaquinasLista.find(it => {
      return it.label === item.tipo_aquisicao;
    });
    setTipoAquisicao({
      value: String(aquisicao?.value),
      label: aquisicao?.label || '-',
    });
    setDescricao(item.descricao);
    setFornecedor(item.fornecedor);
    setQuantidade(item.quantidade);
    setIdEdicao(item.id);
  };

  const atualizarParque = () => {
    if (parqueAtivo === 'true') {
      onCloseDialog();
      return;
    }

    setFetching(true);
    api
      .put(`/parque_maquinas/${idEdicao}/inativar`, {})
      .then(() => {
        toast({
          title: 'Parque de máquinas atualizado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setFetching(false);
        loadParqueMaquinas();
        loadTotalParqueMaquinas();
        onCloseDialog();
      })
      .catch(() => {
        toast({
          title: 'Erro ao atualizar parque de máquinas',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setFetching(false);
      });
  };

  const handleCreateFabricante = (inputValue: string) => {
    if (inputValue.length > 60) {
      toast({
        title: 'O nome do fabricante deve ter no máximo 60 caracteres',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    api.post(`/fabricantes`, { nome: inputValue }).then(() => {
      refetchFabricante();
    });
  };

  return (
    <>
      <Button
        color="white"
        background="gray.900"
        _hover={{
          bgColor: 'black',
        }}
        onClick={handleOpen}
        isDisabled={!grupo}
      >
        Cadastrar Parque de Máquinas
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="xl"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent
          borderTopLeftRadius="md"
          sx={{ maxWidth: '50vw', width: '50vw' }}
        >
          <DrawerCloseButton />
          <DrawerHeader>Cadastrar Parque de Máquinas</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={2}>
              <Stack>
                <FormControl isInvalid={errors.filial}>
                  <ReactSelect
                    name="filial"
                    isSearchable
                    label="Filial"
                    placeholder="Selecione"
                    error={errors.filial}
                    options={filialList}
                    value={filial ?? null}
                    onChange={e => handleFilial(e as SelectItem)}
                    defaultStyle
                    isDisabled={isFetching || isFilialLoading}
                    defaultValue={
                      !isEdit
                        ? undefined
                        : filialList?.length === 1
                        ? filialList[0].value
                        : undefined
                    }
                    isClearable
                    isLoading={isFilialLoading}
                    noOptionsText="Não há opções"
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: errors.filial ? 'red' : '',
                      }),
                    }}
                  />
                </FormControl>
              </Stack>
              <HStack alignItems="baseline">
                <FormControl isInvalid={errors.fabricante} maxW="33%">
                  <FormLabel fontSize="14px" marginBottom={2}>
                    Fabricante
                  </FormLabel>
                  <CreatableSelect
                    id="fabricante"
                    name="fabricante"
                    isClearable
                    onChange={e => handleFabricante(e as SelectItem)}
                    onCreateOption={handleCreateFabricante}
                    options={formattedFabricanteList}
                    value={fabricante ?? null}
                    defaultValue={
                      !isEdit
                        ? undefined
                        : formattedFabricanteList?.length === 1
                        ? formattedFabricanteList[0]
                        : undefined
                    }
                    isLoading={isFabricanteLoading}
                    formatCreateLabel={inputValue => `Criar: ${inputValue}`}
                    placeholder="Selecione"
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: errors.fabricante ? 'red' : '',
                      }),
                    }}
                    noOptionsMessage={() => 'Não há opções'}
                  />
                </FormControl>
                <FormControl isInvalid={errors.quantidade}>
                  <Input
                    type="text"
                    isRequired
                    label="Quantidade"
                    error={errors.quantidade}
                    fontSize="15px"
                    maxLength={12}
                    value={
                      quantidade
                        ? Number(quantidade).toLocaleString('pt-BR', {
                            maximumFractionDigits: 0,
                          })
                        : 0
                    }
                    name="quantidade"
                    onChange={e => handleQuantidade(e.target.value)}
                    isDisabled={isFetching}
                  />
                </FormControl>
                <FormControl isInvalid={errors.idadeParque}>
                  <ReactSelect
                    name="idadeParque"
                    label="Idade do Parque"
                    placeholder="Selecione"
                    error={errors.idadeParque}
                    options={idadeParqueList}
                    value={idadeParque ?? null}
                    onChange={e => handleIdadeParque(e as SelectItem)}
                    defaultStyle
                    isDisabled={isFetching}
                    defaultValue={
                      !isEdit
                        ? undefined
                        : idadeParqueList?.length === 1
                        ? idadeParqueList[0].value
                        : undefined
                    }
                    isClearable
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: errors.idadeParque ? 'red' : '',
                      }),
                    }}
                  />
                </FormControl>
              </HStack>
              <HStack alignItems="baseline">
                <FormControl isInvalid={errors.tipoEquipamento} width="30%">
                  <ReactSelect
                    name="tipoEquipamento"
                    label="Tipo de equipamento"
                    placeholder="Selecione"
                    error={errors.tipoEquipamento}
                    options={tipoEquipamentoList}
                    value={tipoEquipamento ?? null}
                    onChange={e => handleTipoEquipamento(e as SelectItem)}
                    defaultStyle
                    isDisabled={isFetching || isTipoEquipamentoLoading}
                    defaultValue={
                      !isEdit
                        ? undefined
                        : tipoEquipamentoList?.length === 1
                        ? tipoEquipamentoList[0].value
                        : undefined
                    }
                    isClearable
                    isLoading={isTipoEquipamentoLoading}
                    noOptionsText="Não há opções"
                    required
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: errors.tipoEquipamento ? 'red' : '',
                      }),
                    }}
                  />
                </FormControl>
                <FormControl isInvalid={errors.descricao}>
                  <Input
                    type="text"
                    isRequired
                    label="Descrição"
                    error={errors.descricao}
                    fontSize="15px"
                    maxLength={255}
                    value={descricao}
                    name="descricao"
                    onChange={e => handleDescricao(e.target.value)}
                    isDisabled={isFetching}
                  />
                </FormControl>
              </HStack>
              <HStack alignItems="baseline">
                <FormControl isInvalid={errors.tipoAquisicao} width="30%">
                  <ReactSelect
                    name="tipoAquisicao"
                    label="Tipo de aquisição"
                    placeholder="Selecione"
                    error={errors.tipoAquisicao}
                    options={tipoAquisicaoList}
                    value={tipoAquisicao ?? null}
                    onChange={e => handleTipoAquisicao(e as SelectItem)}
                    defaultStyle
                    isDisabled={isFetching}
                    defaultValue={
                      !isEdit
                        ? undefined
                        : tipoAquisicaoList?.length === 1
                        ? tipoAquisicaoList[0].value
                        : undefined
                    }
                    isClearable
                    noOptionsText="Não há opções"
                    required
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: errors.tipoAquisicao ? 'red' : '',
                      }),
                    }}
                  />
                </FormControl>
                <FormControl isInvalid={errors.fornecedor}>
                  <Input
                    type="text"
                    label="Fornecedor atual"
                    error={errors.fornecedor}
                    fontSize="15px"
                    maxLength={255}
                    value={fornecedor}
                    name="fornecedor"
                    onChange={e => handleFornecedor(e.target.value)}
                    isDisabled={isFetching}
                  />
                </FormControl>
              </HStack>
            </Stack>
            <Flex justifyContent="end" mt={2}>
              <Button
                type="submit"
                onClick={limpar}
                color="black"
                background="transparent"
                style={{
                  marginTop: '20px',
                  marginRight: '10px',
                }}
              >
                Limpar
              </Button>
              <Button
                type="submit"
                onClick={handleSubmit(handleCreate)}
                color="white"
                background="gray.900"
                _hover={{
                  bgColor: 'black',
                }}
                style={{ marginTop: '20px' }}
                isDisabled={isFetching}
              >
                Adicionar
              </Button>
            </Flex>
            <Stack>
              <FormControl>
                <FormLabel
                  htmlFor="writeUpFile"
                  style={{ fontSize: '14px' }}
                  justifyContent="initial"
                  display="flex"
                >
                  Parque de Máquinas do Grupo
                </FormLabel>
              </FormControl>
            </Stack>
            <Stack>
              <TableContainer>
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th w="10%">FILIAL</Th>
                      <Th w="10%">FABRICANTE</Th>
                      <Th w="10%">TIPO</Th>
                      <Th w="10%">DESCRIÇÃO</Th>
                      <Th w="5%">IDADE</Th>
                      <Th w="5%" textAlign="right">
                        QTDE
                      </Th>
                      <Th w="10%">AQUISIÇÃO</Th>
                      <Th w="10%">FORNECEDOR ATUAL</Th>
                      <Th w="10%">ÚLTIMA ATUALIZAÇÃO</Th>
                      <Th w="10%">STATUS</Th>
                      <Th w="10%">AÇÕES</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {parqueMaquinas && parqueMaquinas.results?.length > 0 ? (
                      parqueMaquinas?.results.map(item => (
                        <Tr key={item.id} verticalAlign="text-top">
                          <Td whiteSpace="pre-wrap" maxW="200px" minW="200px">
                            {item.empresa?.cnpjFormatado} -{' '}
                            {item.empresa?.nm_razaosocial}
                          </Td>
                          <Td whiteSpace="pre-wrap" maxW="200px" minW="200px">
                            {item.fabricante?.nome ?? 'Desconhecido'}
                          </Td>
                          <Td>{item.tipo_equipamento.nm_tipoequipamento}</Td>
                          <Td whiteSpace="pre-wrap" maxW="200px" minW="200px">
                            {item.descricao}
                          </Td>
                          <Td>{item.idade ?? '-'}</Td>
                          <Td textAlign="right">
                            {Number(item.quantidade).toLocaleString('pt-BR', {
                              maximumFractionDigits: 0,
                            })}
                          </Td>
                          <Td>{item.tipo_aquisicao}</Td>
                          <Td whiteSpace="pre-wrap" maxW="200px" minW="200px">
                            {item.fornecedor || '-'}
                          </Td>
                          <Td>
                            {item.dataFormatada}
                            <p>{item.usuario}</p>
                          </Td>
                          <Td>
                            <StatusBadge variant={item.statusColor}>
                              {item.statusStr}
                            </StatusBadge>
                          </Td>
                          <Td>
                            <HStack>
                              {item.status === 1 && (
                                <IconButton
                                  aria-label="Editar"
                                  icon={<Icon as={FaEdit} />}
                                  size="sm"
                                  onClick={() => {
                                    setIsEdit(true);
                                    editar(item);
                                  }}
                                  isDisabled={isEdit}
                                />
                              )}
                              <IconButton
                                aria-label="Remover"
                                icon={<Icon as={FaTrash} />}
                                size="sm"
                                onClick={() => {
                                  setIdRemover(item.id!);
                                  onOpenConfirmation();
                                }}
                                isDisabled={isEdit}
                              />
                              {item.idade === 'Acima de 5 anos' &&
                                item.status === 1 && (
                                  <IconButton
                                    aria-label="Atualizar Parque de Máquinas"
                                    icon={<Icon as={GrUpdate} />}
                                    size="sm"
                                    onClick={() => {
                                      setIdEdicao(item.id);
                                      onOpenDialog();
                                    }}
                                    isDisabled={isEdit}
                                  />
                                )}
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td
                          textAlign="center"
                          align="center"
                          valign="middle"
                          h="100%"
                          colSpan={11}
                        >
                          Nenhum parque de máquinas cadastrado nesse grupo
                        </Td>
                      </Tr>
                    )}
                    {parqueMaquinas &&
                      parqueMaquinas?.results.length > 0 &&
                      page >=
                        Math.ceil((parqueMaquinas?.count || 0) / PAGE_SIZE) && (
                        <Tr>
                          <Td
                            textAlign="right"
                            valign="middle"
                            h="100%"
                            fontWeight="bold"
                            colSpan={5}
                          >
                            Total
                          </Td>
                          <Td
                            textAlign="right"
                            valign="middle"
                            h="100%"
                            fontWeight="bold"
                          >
                            {Number(total).toLocaleString('pt-BR', {
                              maximumFractionDigits: 0,
                            })}
                          </Td>
                        </Tr>
                      )}
                  </Tbody>
                </Table>
              </TableContainer>
              <Flex justify="flex-end">
                <Pagination
                  totalCountOfRegisters={parqueMaquinas?.count || 0}
                  currentPage={page}
                  onPageChange={e => {
                    setPage(e);
                    loadParqueMaquinas(e);
                    loadTotalParqueMaquinas();
                  }}
                />
              </Flex>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Stack>
        <ConfirmationDialog
          confirmar={atualizarParque}
          onClose={onCloseDialog}
          isOpen={isOpenDialog}
          titulo="Atualizar Parque de Máquinas"
          btnConfirmText="Salvar"
          colorfulBar={false}
        >
          <span>
            Este parque de máquinas existe há mais de 5 anos. Ainda está em
            funcionamento?
          </span>
          <RadioGroup
            value={parqueAtivo}
            onChange={e => setParqueAtivo(e)}
            colorScheme="b"
          >
            <Stack spacing={4}>
              <Radio value="true">Sim, o parque continua ativo.</Radio>
              <Radio value="false">Não, o parque não está mais ativo.</Radio>
            </Stack>
          </RadioGroup>
        </ConfirmationDialog>
      </Stack>
      <Stack>
        <ConfirmationDialog
          confirmar={removeMachineParkFunc}
          onClose={onCloseConfirmation}
          isOpen={isOpenConfirmation}
          titulo="Remover Parque de Máquinas"
          btnConfirmText="Confirmar"
          isDisabled={isFetching}
        >
          <span>Tem certeza que deseja remover esse parque de máquinas?</span>
        </ConfirmationDialog>
      </Stack>
    </>
  );
};
